import Box from "@mui/material/Box"
import { LinkOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { CustomerCreditLimitItem } from "./CustomerCreditLimitItem"
import { CustomerPaymentTermItem } from "./CustomerPaymentTermItem"
import PaymentTermsActions from "./PaymentTermsActions"
import { VendorCreditLimitItem } from "./VendorCreditLimitItem"
import { VendorPaymentTermItem } from "./VendorPaymentTermItem"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import type { CompanyAccountingTerm } from "@/graphql/codegen/graphql"
import { COMPANY_TYPE } from "@/screens/Companies/constants"
import { usePaymentTermsContext } from "@/screens/CompanySettings/Accounting/contexts/PaymentTermsContext"

interface PaymentTermsViewProps {
  accountingTermsList: CompanyAccountingTerm[]
  onOpenDialog: () => void
}

const findByCompanyType = (companyType: string) => (term: CompanyAccountingTerm) => term.companyType === companyType

export const PaymentTermsView: FC<PaymentTermsViewProps> = ({ accountingTermsList, onOpenDialog }) => {
  const companyPaymentList = accountingTermsList
  const { isCompanyVendor, isCompanyCustomer } = usePaymentTermsContext()

  const vendorPaymentTerm = companyPaymentList.find(findByCompanyType(COMPANY_TYPE.VENDOR))
  const customerPaymentTerm = companyPaymentList.find(findByCompanyType(COMPANY_TYPE.CUSTOMER))

  const isBothVendorAndCustomer = isCompanyVendor && isCompanyCustomer
  const isVendorOnly = vendorPaymentTerm && isCompanyVendor && !isCompanyCustomer
  const isCustomerOnly = customerPaymentTerm && isCompanyCustomer && !isCompanyVendor

  // Missing payment terms scenarios - only show alert if company is configured for that type
  const showMissingCustomerAlert = isCompanyCustomer && !customerPaymentTerm
  const showMissingVendorAlert = isCompanyVendor && !vendorPaymentTerm
  const showMissingDataAlert =
    (showMissingCustomerAlert || showMissingVendorAlert) && !(vendorPaymentTerm || customerPaymentTerm)

  return (
    <Box className='relative flex w-full items-center rounded border border-gray-300 px-6 py-4'>
      {isBothVendorAndCustomer && (
        <>
          {vendorPaymentTerm || customerPaymentTerm ? (
            <>
              <Box className='flex-1'>
                <VendorPaymentTermItem vendorPaymentTerm={vendorPaymentTerm} />
                <VendorCreditLimitItem vendorPaymentTerm={vendorPaymentTerm} />
              </Box>
              <Box className='flex-1'>
                <CustomerPaymentTermItem customerPaymentTerm={customerPaymentTerm} />
                <CustomerCreditLimitItem customerPaymentTerm={customerPaymentTerm} />
              </Box>
            </>
          ) : null}

          {showMissingDataAlert && (
            <Box className='flex-1'>
              <InformationMissingAlert
                className='px-2 py-4'
                title={
                  showMissingCustomerAlert
                    ? "Add borrowing payment terms and credit limits"
                    : "Add company’s lending payment terms and credit limits"
                }
                titleClassName='text-sm leading-4 pb-0'
                action={
                  <Button
                    color='inherit'
                    size='small'
                    appearance='outlined'
                    className='mr-3 text-primary'
                    startIcon={<LinkOutlined />}
                    onClick={onOpenDialog}
                  >
                    Add Details
                  </Button>
                }
              />
            </Box>
          )}
        </>
      )}
      {isVendorOnly && (
        <Box className='mb-2 mt-4 flex w-full flex-col gap-4'>
          <Box className='flex w-full flex-row'>
            <VendorPaymentTermItem vendorPaymentTerm={vendorPaymentTerm} />
            <VendorCreditLimitItem vendorPaymentTerm={vendorPaymentTerm} />
          </Box>
        </Box>
      )}
      {isCustomerOnly && (
        <Box className='mb-2 mt-4 flex w-full flex-col gap-4'>
          <Box className='flex w-full flex-row'>
            <CustomerPaymentTermItem customerPaymentTerm={customerPaymentTerm} />
            <CustomerCreditLimitItem customerPaymentTerm={customerPaymentTerm} />
          </Box>
        </Box>
      )}
      <div className='w-8 self-start text-right'>
        <PaymentTermsActions onEdit={onOpenDialog} />
      </div>
    </Box>
  )
}
